/**
 * Panel de Flujo Operativo — Ejecución Continua
 *
 * Consume:
 *   /api/flujo/* (sprint operativo · cotización, hoja, inventario, personal, cierre)
 *   /api/erp/* (contrato automático, sincronización agenda, finanzas, timeline, PDFs)
 *   /api/eventos/{id}/pdf/{tipo} para descargas
 *
 * Todo pivota sobre `evento.evento_id`. NO recaptura información.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, FileSignature, ClipboardList, Package, UserCog, CreditCard,
  CheckCircle2, Download, RefreshCcw, Calendar, AlertTriangle, Sparkles
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import FinanzasEventoPanel from "./FinanzasEventoPanel";
import TimelineEvento from "./TimelineEvento";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLUJO = `${API}/flujo`;
const ERP = `${API}/erp`;

const Badge = ({ ok, children }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-700"}`}>
    {children}
  </span>
);

const PASOS = [
  { key: "cotizacion", label: "Cotización", icon: FileText, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "contrato",   label: "Contrato",   icon: FileSignature, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { key: "hoja",       label: "Hoja Operativa", icon: ClipboardList, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "inventario", label: "Inventario", icon: Package, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { key: "personal",   label: "Personal",   icon: UserCog, color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { key: "pagos",      label: "Pagos",      icon: CreditCard, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "cierre",     label: "Cierre",     icon: CheckCircle2, color: "bg-rose-50 text-rose-700 border-rose-200" },
];

export default function FlujoOperativoPanel({ evento, onRefresh }) {
  const evId = evento?.evento_id;
  const yaFinalizado = evento?.estatus === "finalizado";
  const [cargando, setCargando] = useState({});
  const [estados, setEstados] = useState({
    cotizaciones: 0, contrato: null, hoja: false,
    inventario: 0, personal: 0, pagos: 0,
  });
  const [personalCatalogo, setPersonalCatalogo] = useState([]);
  const [personalForm, setPersonalForm] = useState({ personal_id: "", puesto: "chef", tarifa: 0 });
  const [cierreForm, setCierreForm] = useState({
    consumos_reales: "", horas_personal: "", incidencias: "", observaciones: "",
  });

  // Cargar estado consolidado
  const cargarEstado = useCallback(async () => {
    if (!evId) return;
    try {
      const [cot, invMov, contrRes, hojaRes, persAsg, personal, pagosRes] = await Promise.all([
        axios.get(`${FLUJO}/eventos/${evId}/cotizaciones`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${FLUJO}/eventos/${evId}/inventario-movimientos`).catch(() => ({ data: { movimientos: [] } })),
        axios.get(`${API}/contratos`).catch(() => ({ data: [] })),
        axios.get(`${API}/eventos/${evId}/hoja-operativa`).catch(() => ({ data: null })),
        axios.get(`${API}/personal`).catch(() => ({ data: [] })),
        axios.get(`${API}/personal`).catch(() => ({ data: [] })),
        axios.get(`${API}/pagos`).catch(() => ({ data: [] })),
      ]);
      const contratoEvento = (contrRes.data || []).find(c => c.evento_id === evId);
      const personalAsignado = (persAsg.data || []).filter(p => (p.eventos_asignados || []).includes(evId));
      setEstados({
        cotizaciones: cot.data.total || 0,
        contrato: contratoEvento || null,
        hoja: !!hojaRes.data && !!hojaRes.data.sheet_id,
        inventario: (invMov.data.movimientos || []).length,
        personal: personalAsignado.length,
        pagos: (pagosRes.data || []).filter(p => p.evento_id === evId && !p.cancelado).length,
      });
      setPersonalCatalogo(personal.data || []);
    } catch { /* silencio */ }
  }, [evId]);

  useEffect(() => { cargarEstado(); }, [cargarEstado]);

  const conProgreso = async (key, accion, fn) => {
    setCargando(p => ({ ...p, [key]: true }));
    try {
      const res = await fn();
      if (res !== null) toast.success(`${accion}: OK`);
      await cargarEstado();
      onRefresh?.();
      return res;
    } catch (e) {
      toast.error(`${accion}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCargando(p => ({ ...p, [key]: false }));
    }
  };

  // ── ACCIONES OPERATIVAS ──────────────────────────────────────

  const generarCotizacion = () => conProgreso("cot", "Generar cotización", async () => {
    const items = evento?.servicios_asignados || evento?.items || [];
    if (items.length === 0) throw new Error("El evento no tiene items. Agrégalos en Servicios.");
    const { data } = await axios.post(`${FLUJO}/cotizaciones`, {
      evento_id: evId, items, notas: "Cotización generada desde ficha del evento",
    });
    return data;
  });

  const generarContrato = () => conProgreso("contr", "Generar contrato", async () => {
    const { data } = await axios.post(`${ERP}/eventos/${evId}/generar-contrato`);
    if (data.estado === "ya_existia") toast.info("Ya existía un contrato para este evento");
    return data;
  });

  const generarHoja = () => conProgreso("hoja", "Generar hoja operativa", async () => {
    const { data } = await axios.post(`${FLUJO}/eventos/${evId}/generar-hoja-operativa`);
    return data;
  });

  const reservarInv = () => conProgreso("inv", "Reservar inventario", async () => {
    const { data } = await axios.post(`${FLUJO}/eventos/${evId}/reservar-inventario`);
    if (data.sin_catalogo_id?.length > 0) toast.warning(`${data.sin_catalogo_id.length} items sin catalogo_id`);
    return data;
  });

  const sincronizarAgenda = () => conProgreso("sync", "Sincronizar agenda", async () => {
    // Genera agenda si no existe
    try { await axios.post(`${API}/eventos/${evId}/agenda/generar`); } catch { /* puede ya existir */ }
    const { data } = await axios.post(`${ERP}/eventos/${evId}/sincronizar-agenda`);
    return data;
  });

  const asignarPersonal = () => conProgreso("per", "Asignar personal", async () => {
    if (!personalForm.personal_id) throw new Error("Selecciona un miembro del personal.");
    const { data } = await axios.post(`${FLUJO}/eventos/${evId}/asignar-personal`, {
      user_id: personalForm.personal_id,
      puesto: personalForm.puesto,
      tarifa: parseFloat(personalForm.tarifa) || 0,
    });
    setPersonalForm({ personal_id: "", puesto: "chef", tarifa: 0 });
    return data;
  });

  const ejecutarCierre = () => conProgreso("cierre", "Cerrar evento", async () => {
    if (!window.confirm("¿Confirmas cerrar este evento? Se calculará utilidad y se liberará inventario sobrante.")) return null;
    let consumos = [], horas = [];
    try {
      if (cierreForm.consumos_reales.trim()) consumos = JSON.parse(cierreForm.consumos_reales);
      if (cierreForm.horas_personal.trim()) horas = JSON.parse(cierreForm.horas_personal);
    } catch {
      throw new Error("Formato JSON inválido en consumos u horas.");
    }
    const { data } = await axios.post(`${FLUJO}/eventos/${evId}/cierre`, {
      consumos_reales: consumos, horas_personal: horas,
      incidencias: cierreForm.incidencias, observaciones: cierreForm.observaciones,
    });
    toast.success(`Cierre OK. Utilidad: $${data.estadisticas.utilidad} · Margen: ${data.estadisticas.margen_pct}%`);
    return data;
  });

  const descargarPDF = (tipo) => async () => {
    try {
      const url = `${API}/eventos/${evId}/pdf/${tipo}?download=1`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error(`No se pudo descargar PDF ${tipo}`);
    }
  };

  const stepDone = {
    cotizacion: estados.cotizaciones > 0,
    contrato: !!estados.contrato,
    hoja: estados.hoja,
    inventario: estados.inventario > 0,
    personal: estados.personal > 0,
    pagos: estados.pagos > 0,
    cierre: yaFinalizado,
  };

  return (
    <div className="space-y-6" data-testid="flujo-operativo-panel">
      {/* PIPELINE VISUAL */}
      <div className="flex flex-wrap gap-2" data-testid="flujo-pipeline">
        {PASOS.map((p, i) => {
          const done = stepDone[p.key];
          return (
            <div key={p.key} className={`flex-1 min-w-[110px] p-3 rounded-lg border flex items-center gap-2 ${done ? "bg-emerald-50 border-emerald-300" : p.color}`} data-testid={`paso-${p.key}`}>
              <p.icon className="w-4 h-4 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider opacity-70">Paso {i+1}</p>
                <p className="text-sm font-medium truncate">{p.label}</p>
              </div>
              {done && <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto shrink-0" />}
            </div>
          );
        })}
      </div>

      {/* GRID 2 COLUMNAS: ACCIONES + PANELES DERIVADOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* IZQUIERDA · ACCIONES */}
        <div className="space-y-3">
          {/* 1. Cotización */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> 1 · Cotización <Badge ok={stepDone.cotizacion}>{estados.cotizaciones}</Badge></CardTitle></CardHeader>
            <CardContent className="pt-0 flex gap-2 flex-wrap">
              <Button size="sm" data-testid="btn-generar-cotizacion" onClick={generarCotizacion} disabled={cargando.cot || yaFinalizado}>
                {cargando.cot ? "…" : "Generar"}
              </Button>
              <Button size="sm" variant="outline" data-testid="btn-pdf-cotizacion" onClick={descargarPDF("cotizacion")}>
                <Download className="w-3 h-3 mr-1" /> PDF
              </Button>
            </CardContent>
          </Card>

          {/* 2. Contrato */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileSignature className="w-4 h-4" /> 2 · Contrato <Badge ok={stepDone.contrato}>{estados.contrato ? "existe" : "no"}</Badge></CardTitle></CardHeader>
            <CardContent className="pt-0 flex gap-2 flex-wrap">
              <Button size="sm" data-testid="btn-generar-contrato" onClick={generarContrato} disabled={cargando.contr || yaFinalizado}>
                {cargando.contr ? "…" : (estados.contrato ? "Regenerar" : "Generar")}
              </Button>
              <Button size="sm" variant="outline" data-testid="btn-pdf-contrato" onClick={descargarPDF("contrato")} disabled={!estados.contrato}>
                <Download className="w-3 h-3 mr-1" /> PDF
              </Button>
            </CardContent>
          </Card>

          {/* 3. Agenda + Hoja */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4" /> 3 · Agenda + Hoja Operativa <Badge ok={stepDone.hoja}>{stepDone.hoja ? "ok" : "no"}</Badge></CardTitle></CardHeader>
            <CardContent className="pt-0 flex gap-2 flex-wrap">
              <Button size="sm" data-testid="btn-sync-agenda" onClick={sincronizarAgenda} disabled={cargando.sync}>
                {cargando.sync ? "…" : "Sincronizar agenda"}
              </Button>
              <Button size="sm" data-testid="btn-generar-hoja" onClick={generarHoja} disabled={cargando.hoja || yaFinalizado}>
                {cargando.hoja ? "…" : "Generar hoja"}
              </Button>
              <Button size="sm" variant="outline" data-testid="btn-pdf-hoja" onClick={descargarPDF("hoja_operativa")}>
                <Download className="w-3 h-3 mr-1" /> PDF
              </Button>
            </CardContent>
          </Card>

          {/* 4. Inventario */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Package className="w-4 h-4" /> 4 · Inventario <Badge ok={stepDone.inventario}>{estados.inventario} movs</Badge></CardTitle></CardHeader>
            <CardContent className="pt-0">
              <Button size="sm" data-testid="btn-reservar-inv" onClick={reservarInv} disabled={cargando.inv || yaFinalizado}>
                {cargando.inv ? "…" : "Reservar inventario"}
              </Button>
            </CardContent>
          </Card>

          {/* 5. Personal */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><UserCog className="w-4 h-4" /> 5 · Personal <Badge ok={stepDone.personal}>{estados.personal} asig.</Badge></CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Miembro</Label>
                  <select
                    data-testid="select-personal-id"
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={personalForm.personal_id}
                    onChange={e => setPersonalForm(f => ({ ...f, personal_id: e.target.value }))}
                  >
                    <option value="">— Selecciona —</option>
                    {personalCatalogo.map(p => (
                      <option key={p.personal_id} value={p.personal_id}>{p.nombre} {p.puestos?.[0] ? `· ${p.puestos[0]}` : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Puesto</Label>
                  <select
                    data-testid="select-personal-puesto"
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={personalForm.puesto}
                    onChange={e => setPersonalForm(f => ({ ...f, puesto: e.target.value }))}
                  >
                    <option value="chef">Chef</option>
                    <option value="coordinador">Coordinador</option>
                    <option value="mesero">Mesero</option>
                    <option value="auxiliar">Auxiliar</option>
                    <option value="tecnico">Técnico</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Tarifa/h</Label>
                  <Input data-testid="input-personal-tarifa" type="number" size={1} value={personalForm.tarifa}
                    onChange={e => setPersonalForm(f => ({ ...f, tarifa: e.target.value }))} />
                </div>
              </div>
              <Button size="sm" className="w-full" data-testid="btn-asignar-personal" onClick={asignarPersonal} disabled={cargando.per || yaFinalizado}>
                {cargando.per ? "Asignando…" : "Asignar personal"}
              </Button>
              {personalCatalogo.length === 0 && (
                <p className="text-xs text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> No hay personal registrado. Ve a "Personal" para agregar.
                </p>
              )}
            </CardContent>
          </Card>

          {/* 6. Pagos */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CreditCard className="w-4 h-4" /> 6 · Pagos <Badge ok={stepDone.pagos}>{estados.pagos} registrados</Badge></CardTitle></CardHeader>
            <CardContent className="pt-0">
              <Button size="sm" variant="outline" data-testid="btn-ir-pagos" onClick={() => {
                const btn = document.querySelector('[data-testid="tab-general"]') || document.querySelector('[data-testid="tab-resumen"]');
                btn?.click();
                window.scrollTo({ top: 0, behavior: "smooth" });
                toast.info("Ve al bloque de Pagos en la pestaña principal");
              }}>Ir a registro de pagos →</Button>
            </CardContent>
          </Card>

          {/* 7. Cierre */}
          <Card className={yaFinalizado ? "border-emerald-300 bg-emerald-50" : "border-amber-300"}>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> 7 · Cierre <Badge ok={yaFinalizado}>{yaFinalizado ? "finalizado" : "pendiente"}</Badge></CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-2">
              {yaFinalizado ? (
                <p className="text-sm text-emerald-800">
                  <Sparkles className="w-4 h-4 inline mr-1" />
                  Evento finalizado. Revisa las estadísticas y la utilidad en Finanzas.
                </p>
              ) : (
                <>
                  <Label className="text-xs">Consumos reales (JSON, opcional)</Label>
                  <Textarea data-testid="input-cierre-consumos" rows={2}
                    placeholder='[{"catalogo_id":"cat_xxx","cantidad_real":8,"costo_real":3200}]'
                    value={cierreForm.consumos_reales}
                    onChange={e => setCierreForm(f => ({ ...f, consumos_reales: e.target.value }))} />
                  <Label className="text-xs">Horas del personal (JSON, opcional)</Label>
                  <Textarea data-testid="input-cierre-horas" rows={2}
                    placeholder='[{"personal_id":"per_xxx","horas":5,"tarifa_final":500}]'
                    value={cierreForm.horas_personal}
                    onChange={e => setCierreForm(f => ({ ...f, horas_personal: e.target.value }))} />
                  <Input data-testid="input-cierre-incidencias" placeholder="Incidencias (opcional)" value={cierreForm.incidencias}
                    onChange={e => setCierreForm(f => ({ ...f, incidencias: e.target.value }))} />
                  <Input data-testid="input-cierre-observaciones" placeholder="Observaciones (opcional)" value={cierreForm.observaciones}
                    onChange={e => setCierreForm(f => ({ ...f, observaciones: e.target.value }))} />
                  <Button size="sm" className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="btn-ejecutar-cierre" onClick={ejecutarCierre} disabled={cargando.cierre}>
                    {cargando.cierre ? "Cerrando…" : "Ejecutar cierre"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* DERECHA · FINANZAS + TIMELINE */}
        <div className="space-y-4">
          <FinanzasEventoPanel eventoId={evId} />
          <TimelineEvento eventoId={evId} />
        </div>
      </div>
    </div>
  );
}
